<script setup lang="ts">
import { computed } from 'vue'
import { AdsIcon } from '@accor/ads-components'
import { IconChevronDown } from '@accor/icons'
import { useI18n } from 'vue-i18n'
import SideNavLink from '@/components/atoms/SideNavLink.vue'
import type { MenuEntry } from '@/types/navigation'

const props = defineProps<{
  entry: MenuEntry
  isOpen: boolean
  isActive: (entry: MenuEntry) => boolean
  getHref: (entry: MenuEntry) => string
}>()

const emit = defineEmits<{
  toggle: [event: Event, label: string]
  click: [entry: MenuEntry]
  'set-ref': [el: Element | null, label: string]
}>()

const { t } = useI18n()

const label = computed(() => t(props.entry.i18nLabel))
const handleToggle = (event: Event) => emit('toggle', event, props.entry.i18nLabel)
const handleClick = (child: MenuEntry) => emit('click', child)
const handleSetRef = (el: Element | null) => emit('set-ref', el, props.entry.i18nLabel)
</script>

<template>
  <details :open="props.isOpen" data-testid="nav-category" @toggle.stop="handleToggle">
    <summary class="side-nav__first-link-item">
      <div class="side-nav__link-content side-nav__first-level">
        <span class="side-nav__list-label">{{ label }}</span>
        <AdsIcon :icon="IconChevronDown" size="sm" aria-hidden="true" class="side-nav__chevron" />
      </div>
    </summary>

    <ul :ref="(el) => handleSetRef(el as Element | null)" class="side-nav__sub-level">
      <li
        v-for="child in props.entry.children"
        :key="child.i18nLabel"
        class="side-nav__list-item"
        :class="[child.className, { 'side-nav__list-item--selected-white': props.isActive(child) }]"
      >
        <SideNavLink
          :entry="child"
          :is-active="props.isActive(child)"
          :href="props.getHref(child)"
          @click="handleClick"
        />
      </li>
    </ul>
  </details>
</template>

<style lang="scss">
@use '@/styles/utils';

.side-nav {
  &__first-link-item {
    @include ads-sass.body-default-02;
    display: flex;
    align-items: center;
    padding: 0 0 ads.$ads-spacing-inline-sm;
  }

  &__link-item {
    &:is(summary) {
      width: 100%;
      color: inherit;
    }
  }

  &__sub-level {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__chevron {
    transition: transform 0.2s ease;
  }

  details[open] &__chevron {
    transform: rotate(180deg);
  }
}
</style>
